"use client";

// import type { Metadata } from "next";
import "./globals.css";
import Toast from "components/Toast";
import Image from "next/image";
import { ShoppingCart, UserIcon, Settings, Heart, ShoppingBag, LogOut, MessageCircle, Circle } from "lucide-react";
import { Suspense } from "react";

import { Button } from "@/components/ui/Button";
import { Footer2 } from "@/components/layout/Footer";
import { MainSidebar } from "@/components/layout/MainSidebar";
import { Avatar } from "@/components/ui/Avatar";
import SearchBar from "./SearchBar";
import { QueryProvider } from "@/lib/providers/QueryProvider";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { messageStore } from "@/static-stores/messageStore";
import { CartSidebar } from "@/components/cart/CartSidebar";
// import { getMetadata } from "@/lib/stores/MetadataStore";

// export const metadata: Metadata = getMetadata();

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  // Initialize sidebar state - will be updated in useEffect after hydration
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userStatus, setUserStatus] = useState<'online' | 'away' | 'busy' | 'invisible'>('online');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  // Handle hydration and localStorage reading
  useEffect(() => {
    // Read initial sidebar state from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved) {
      setIsSidebarCollapsed(JSON.parse(saved));
    }

    // Mark loading as complete
    setIsLoading(false);

    // Listen for sidebar collapse state changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sidebar-collapsed') {
        setIsSidebarCollapsed(e.newValue ? JSON.parse(e.newValue) : false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check authentication status on mount and listen for changes
  useEffect(() => {
    const checkAuthStatus = () => {
      // In a real app, this would check for JWT tokens, session cookies, etc.
      // For now, we'll check localStorage for a demo auth token
      const token = localStorage.getItem('auth-token');
      setIsAuthenticated(!!token);
    };

    checkAuthStatus();

    // Listen for auth changes (login/logout events)
    const handleAuthChange = () => checkAuthStatus();
    window.addEventListener('auth-change', handleAuthChange);

    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  // Update unread message count
  useEffect(() => {
    const updateUnreadCount = () => {
      setUnreadCount(messageStore.getTotalUnreadCount());
    };

    updateUnreadCount();
    const interval = setInterval(updateUnreadCount, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Track cart item count
  useEffect(() => {
    const updateCartCount = () => {
      // This would normally use the cart store, but for now we'll use localStorage
      const cartData = localStorage.getItem('retro-stall-cart');
      if (cartData) {
        try {
          const cart = JSON.parse(cartData);
          const totalItems = cart.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0;
          setCartItemCount(totalItems);
        } catch (error) {
          console.error('Error parsing cart data:', error);
          setCartItemCount(0);
        }
      } else {
        setCartItemCount(0);
      }
    };

    updateCartCount();

    // Listen for cart changes
    const handleCartChange = () => {
      console.log('🛒 Layout: Cart changed, updating count');
      updateCartCount();
    };

    window.addEventListener('cart-updated', handleCartChange);
    window.addEventListener('storage', (e) => {
      if (e.key === 'retro-stall-cart') {
        updateCartCount();
      }
    });

    return () => {
      window.removeEventListener('cart-updated', handleCartChange);
    };
  }, []);

  // Listen for cart item added events to open sidebar
  useEffect(() => {
    const handleCartItemAdded = (event: CustomEvent) => {
      console.log('🛒 Layout received cart-item-highlight event:', event.detail);
      console.log('🛒 Opening cart sidebar...');
      setIsCartOpen(true);
    };

    console.log('🎧 Layout listening for cart-item-highlight events');
    window.addEventListener('cart-item-highlight', handleCartItemAdded as EventListener);

    return () => {
      console.log('🔇 Layout removing event listener');
      window.removeEventListener('cart-item-highlight', handleCartItemAdded as EventListener);
    };
  }, []);



  return (
    <html lang="en">
      <head>
        {/* Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID || 'GTM-XXXXXXX'}');
            `,
          }}
        />
        {/* End Google Tag Manager */}
      </head>
      <body>
        <div className="min-h-[calc(100vh-2rem)] bg-gray-50 flex flex-col">
          {/* Top Header Bar - Only show for non-authenticated users */}


          {/* For authenticated users, show a navbar with search bar and message icon */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 pe-4">
                <div className="lg:hidden">
                  {/* Mobile menu button is handled by Sidebar component */}
                </div>
                  <div className="flex items-center space-x-2">
                    <Image
                      src="/stall-logo.png"
                      alt="Stall Logo"
                      width={40}
                      height={40}
                      style={{ objectFit: 'scale-down' }}
                      className="border shadow-sm rounded cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.location.href = '/'}
                    />
                    <span
                      className="font-bold text-xl text-gray-900 text-nowrap cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => window.location.href = '/'}
                    >
                      RetroStall
                    </span>
                  </div>
              </div>

              <div className="flex items-center space-x-4 grow">

                {/* Collapse/Expand Sidebar Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newState = !isSidebarCollapsed;
                    setIsSidebarCollapsed(newState);
                    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
                    // Trigger custom event to notify Sidebar component
                    window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: newState }));
                  }}
                  className="hidden lg:flex items-center space-x-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors me-0"
                >
                  {/* Burger Icon - Three horizontal lines */}
                  <svg
                    className={`h-5 w-5 transition-all duration-300`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                  <span className="sr-only">{isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}</span>
                </Button>
                <Suspense fallback={<div className="h-16 bg-gray-100 animate-pulse rounded" />}>
                  <SearchBar />
                </Suspense>

                {/* Cart Icon - Always visible for all users */}
                <div
                  className="cursor-pointer p-2 rounded-full hover:bg-gray-100 relative"
                  onClick={() => {
                    console.log('🛒 Layout: Cart icon clicked, count:', cartItemCount);
                    setIsCartOpen(true);
                  }}
                >
                  <div className="group relative">
                    <ShoppingCart className="h-5 w-5 text-gray-600" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg">
                        {cartItemCount > 99 ? '99+' : cartItemCount}
                      </span>
                    )}
                    <span className="group-hover:opacity-100 transition-opacity bg-gray-800 px-2 py-1 text-sm text-gray-100 rounded-md absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 whitespace-nowrap">
                      Cart ({cartItemCount})
                    </span>
                  </div>
                </div>

                {isAuthenticated && (
                  <div className="flex items-center space-x-2">
                    {/* Message Icon with Badge for authenticated users */}
                    <div
                      className="cursor-pointer p-2 rounded-full hover:bg-gray-100 relative"
                      onClick={() => window.location.href = '/messages'}
                    >
                      <div className="group relative">
                        <MessageCircle className="h-5 w-5 text-gray-600" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                        <span className="group-hover:opacity-100 transition-opacity bg-gray-800 px-2 py-1 text-sm text-gray-100 rounded-md absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 whitespace-nowrap">
                          Messages ({unreadCount})
                        </span>
                      </div>
                    </div>

                    {/* Avatar with dropdown for authenticated users */}
                    <Avatar
                      size="md"
                      fallback="JD"
                      showDropdown={true}
                      status={userStatus}
                      showStatusIndicator={true}
                      dropdownItems={[
                        {
                          label: "My Profile",
                          onClick: () => window.location.href = '/profile',
                          icon: UserIcon,
                        },
                        {
                          label: "My Orders",
                          onClick: () => window.location.href = '/orders',
                          icon: ShoppingBag,
                        },
                        {
                          label: "Favorites",
                          onClick: () => window.location.href = '/favorites',
                          icon: Heart,
                        },
                        {
                          label: "Settings",
                          onClick: () => window.location.href = '/settings',
                          icon: Settings,
                        },
                        {
                          label: `Status: ${userStatus.charAt(0).toUpperCase() + userStatus.slice(1)}`,
                          onClick: () => {},
                          icon: Circle,
                          subMenu: [
                            {
                              label: "Set Online",
                              onClick: () => setUserStatus('online'),
                              icon: Circle,
                            },
                            {
                              label: "Set Away",
                              onClick: () => setUserStatus('away'),
                              icon: Circle,
                            },
                            {
                              label: "Set Busy",
                              onClick: () => setUserStatus('busy'),
                              icon: Circle,
                            },
                            {
                              label: "Set Invisible",
                              onClick: () => setUserStatus('invisible'),
                              icon: Circle,
                            },
                          ],
                        },
                        {
                          label: "Logout",
                          onClick: () => window.location.href = '/logout',
                          icon: LogOut,
                          variant: "destructive",
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main Layout Container - Only render after loading is complete */}
          {!isLoading && (
            <div className="flex">
              {/* Sidebar - Fixed Position */}
              <MainSidebar isAuthenticated={isAuthenticated} isCollapsed={isSidebarCollapsed} />

              {/* Main Content - Account for fixed sidebar */}
              <div className={cn(
                "flex-1 flex flex-col min-h-0",
                "lg:ml-0", // Mobile: no margin
                isSidebarCollapsed ? "lg:ml-16" : "lg:ml-60" // Desktop: adjust based on sidebar state
              )}>
                {/* Page Content */}
                <QueryProvider>
                  <main className="flex-1 overflow-auto">
                    {children}
                  </main>
                </QueryProvider>

                <Footer2 />
              </div>
            </div>
          )}


        </div>

        {/* Cart Sidebar - Available for all users */}
        <CartSidebar
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
        />

        <Toast />

        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_ID || 'GTM-XXXXXXX'}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}
      </body>
    </html>
  );
}
